import React, { useState, useEffect } from 'react';
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Header from './components/Header';
import Settings from './components/Settings';
import ConversionRates from './components/ConversionRates';
import TransactionForm from './components/TransactionForm';
import BulkImport from './components/BulkImport';
import BulkImportV3 from './components/v3/BulkImportV3';
import ExcelExportPanel from './components/ExcelExportPanel';
import BudgetCharts from './components/BudgetCharts';
import BudgetChartsV3 from './components/v3/BudgetChartsV3';
import TransactionsList from './components/TransactionsList';
import TransactionsListV3 from './components/v3/TransactionsListV3';
import MonthClosure from './components/MonthClosure';
import { useUIVersion } from './context/UIVersionContext';
import { useGoogleSheetsSync } from './hooks/useGoogleSheetsSync';
import { transactionsApi, conversionRatesApi, migrationApi, googleSheetsApi, excelImportApi, preferencesApi } from './services/api';

// Componente principal de la aplicación
const MainApp = () => {
  const { version: uiVersion } = useUIVersion();
  const BulkImportComp = uiVersion === 'v3' ? BulkImportV3 : BulkImport;
  const BudgetChartsComp = uiVersion === 'v3' ? BudgetChartsV3 : BudgetCharts;
  const TransactionsListComp = uiVersion === 'v3' ? TransactionsListV3 : TransactionsList;
  // Estado para navegación entre páginas
  const [currentPage, setCurrentPage] = useState('dashboard');
  
  // Hook para sincronización con Google Sheets
  const { syncTransactions, syncing } = useGoogleSheetsSync();
  
  // Estado para auto-sincronización
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(() => {
    const saved = localStorage.getItem('saver-auto-sync-enabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  // Estado para mostrar indicador de sincronización
  const [showSyncIndicator, setShowSyncIndicator] = useState(false);
  
  // Estado para las tasas de conversión (1 GBP = X moneda)
  const [conversionRates, setConversionRates] = useState({
    EUR: 1.165,  // 1 GBP = 1.165 EUR
    BRL: 6.36    // 1 GBP = 6.36 BRL
  });

  // Estado de carga
  const [loading, setLoading] = useState(true);
  const [migrationNeeded, setMigrationNeeded] = useState(false);
  
  // Cargar preferencia de auto-sync desde servidor al iniciar
  useEffect(() => {
    preferencesApi.get('autoSyncEnabled').then(value => {
      if (value !== null) {
        setAutoSyncEnabled(value);
        localStorage.setItem('saver-auto-sync-enabled', JSON.stringify(value));
      }
    });
  }, []);

  // Persistir auto-sync en servidor y localStorage cuando cambia
  useEffect(() => {
    localStorage.setItem('saver-auto-sync-enabled', JSON.stringify(autoSyncEnabled));
    preferencesApi.set('autoSyncEnabled', autoSyncEnabled);
  }, [autoSyncEnabled]);

  // Hook para exportación Excel (no se usa directamente aquí, pero se mantiene para compatibilidad)
  // const { saveToExcel } = useExcelExport();

  // Estado para las transacciones
  const [transactions, setTransactions] = useState([]);

  // Cargar datos desde el servidor al iniciar
  useEffect(() => {
    loadDataFromServer();
  }, []);

  const loadDataFromServer = async () => {
    try {
      setLoading(true);

      // Verificar si hay datos en localStorage que necesiten migración
      const localTransactions = localStorage.getItem('saver-transactions');
      const hasLocalData = localTransactions && JSON.parse(localTransactions).length > 0;

      if (hasLocalData) {
        console.log('⚠️ Datos encontrados en localStorage - se requiere migración');
        setMigrationNeeded(true);
      }

      // Cargar transacciones
      const loadedTransactions = await transactionsApi.getAll();
      setTransactions(loadedTransactions);

      // Cargar tasas de conversión
      const loadedRates = await conversionRatesApi.get();
      if (Object.keys(loadedRates).length > 0) {
        setConversionRates(loadedRates);
      }

      console.log(`✅ Datos cargados: ${loadedTransactions.length} transacciones`);
    } catch (error) {
      console.error('❌ Error cargando datos del servidor:', error);
      // Si falla, intentar cargar desde localStorage como fallback
      const savedTransactions = localStorage.getItem('saver-transactions');
      if (savedTransactions) {
        setTransactions(JSON.parse(savedTransactions));
        setMigrationNeeded(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // DEBUG TEMPORAL - Verificar localStorage al cargar
  useEffect(() => {
    console.log('=== DEBUG STORAGE ===');
    
    // Verificar transacciones
    const savedTransactions = localStorage.getItem('saver-transactions');
    if (savedTransactions) {
      try {
        const transactionsData = JSON.parse(savedTransactions);
        console.log('Transacciones guardadas:', transactionsData.length);
        console.log('Transacciones:', transactionsData);
        
        // Verificar fechas
        transactionsData.forEach((t, index) => {
          const date = new Date(t.date);
          console.log(`Transação ${index + 1}:`, {
            merchantName: t.merchantName,
            date: t.date,
            parsedDate: date,
            year: date.getFullYear(),
            month: date.getMonth(), // 0-based
            monthName: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'][date.getMonth()]
          });
        });
      } catch (e) {
        console.error('Error parsing transactions:', e);
      }
    } else {
      console.log('Não há transações salvas');
    }
    
    // Verificar historial de cierre del mes
    const savedClosure = localStorage.getItem('saver-month-closure');
    if (savedClosure) {
      try {
        const closure = JSON.parse(savedClosure);
        console.log('Datos de cierre del mes:', closure);
        console.log('Historial mensual:', closure.monthlyHistory);
      } catch (e) {
        console.error('Error parsing closure data:', e);
      }
    } else {
      console.log('No hay datos de cierre del mes guardados');
    }
    
    // Fecha actual
    const now = new Date();
    console.log('Fecha actual:', {
      date: now,
      year: now.getFullYear(),
      month: now.getMonth(),
      monthName: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'][now.getMonth()]
    });
    
    console.log('=== FIN DEBUG ===');
  }, []);

  // Función para auto-sincronización silenciosa
  const performAutoSync = async (transactionsToSync) => {
    if (!autoSyncEnabled) return;
    
    try {
      setShowSyncIndicator(true);
      console.log('🔄 Auto-sincronizando com Google Sheets...');
      
      await syncTransactions(transactionsToSync);
      
      console.log('✅ Auto-sincronização completada');
      
      // Ocultar indicador después de 2 segundos
      setTimeout(() => setShowSyncIndicator(false), 2000);
      
    } catch (error) {
      console.error('❌ Erro na auto-sincronização:', error);
      setShowSyncIndicator(false);
      // Erro silencioso - não mostrar ao usuário
    }
  };

  // Función para agregar una nueva transacción
  const handleAddTransaction = async (newTransaction) => {
    try {
      // Guardar en el servidor
      await transactionsApi.create(newTransaction);

      // Actualizar estado local
      const updatedTransactions = [...transactions, newTransaction];
      setTransactions(updatedTransactions);

      console.log('✅ Transacción guardada en el servidor');

      // Auto-sincronización con delay pequeño para permitir batch
      setTimeout(() => {
        performAutoSync(updatedTransactions);
      }, 1000);
    } catch (error) {
      console.error('❌ Error guardando transacción:', error);
      alert('Error al guardar la transacción. Por favor, intenta nuevamente.');
    }
  };

  // Función para agregar múltiples transacciones (BulkImport)
  const handleAddMultipleTransactions = async (newTransactions) => {
    try {
      // Guardar en el servidor
      await transactionsApi.createMany(newTransactions);

      // Actualizar estado local
      setTransactions(prev => {
        const updatedTransactions = [...prev, ...newTransactions];

        // Auto-sincronización para bulk import con delay mayor
        setTimeout(() => {
          performAutoSync(updatedTransactions);
        }, 2000);

        return updatedTransactions;
      });

      console.log(`✅ ${newTransactions.length} transacciones guardadas en el servidor`);
    } catch (error) {
      console.error('❌ Error guardando transacciones:', error);
      alert('Error al guardar las transacciones. Por favor, intenta nuevamente.');
    }
  };

  // Función para eliminar una transacción
  const handleDeleteTransaction = async (transactionId) => {
    console.log('App.js - handleDeleteTransaction chamado com ID:', transactionId);
    console.log('Contagem atual de transações:', transactions.length);

    try {
      // Eliminar del servidor
      await transactionsApi.delete(transactionId);

      // Actualizar estado local
      setTransactions(prev => {
        const updatedTransactions = prev.filter(t => t.id !== transactionId);
        console.log('App.js - Após filtragem, nova contagem:', updatedTransactions.length);
        return updatedTransactions;
      });

      console.log('✅ Transacción eliminada del servidor');
    } catch (error) {
      console.error('❌ Error eliminando transacción:', error);
      alert('Error al eliminar la transacción. Por favor, intenta nuevamente.');
    }
  };

  // Función para limpiar completamente el localStorage (para debug)
  const clearAllLocalStorage = () => {
    if (window.confirm('Tem certeza de que deseja limpar TODOS os dados salvos?\n\nIsso inclui transações, taxas de conversão e histórico de fechamento mensal.\n\nEsta ação não pode ser desfeita.')) {
      localStorage.removeItem('saver-transactions');
      localStorage.removeItem('saver-conversion-rates');
      localStorage.removeItem('saver-month-closure');
      
      // Reiniciar estados
      setTransactions([]);
      setConversionRates({
        EUR: 1.165,
        BRL: 6.36
      });
      
      alert('Todos os dados foram eliminados. A página será recarregada.');
      window.location.reload();
    }
  };

  // Función para verificar datos específicos (para debug)
  const debugSpecificData = () => {
    console.log('=== DEBUG ESPECÍFICO ===');
    
    // Verificar que transacciones están en estado actual
    console.log('Estado atual de transações:', transactions);
    
    // Verificar localStorage diretamente
    const savedTransactions = localStorage.getItem('saver-transactions');
    if (savedTransactions) {
      const parsed = JSON.parse(savedTransactions);
      console.log('Transações no localStorage:', parsed);
      
      // Verificar datas de julho especificamente
      const julyTransactions = parsed.filter(t => {
        const date = new Date(t.date);
        return date.getFullYear() === 2025 && date.getMonth() === 6; // Julho = mês 6
      });
      console.log('Transações de julho 2025:', julyTransactions);
      
      // Verificar datas de junho especificamente
      const juneTransactions = parsed.filter(t => {
        const date = new Date(t.date);
        return date.getFullYear() === 2025 && date.getMonth() === 5; // Junho = mês 5
      });
      console.log('Transações de junho 2025:', juneTransactions);
    }
    
    console.log('=== FIN DEBUG ESPECÍFICO ===');
  };

  // Función para actualizar las tasas de conversión
  const handleRatesChange = async (newRates) => {
    try {
      // Guardar en el servidor
      await conversionRatesApi.update(newRates);

      // Actualizar estado local
      setConversionRates(newRates);

      console.log('✅ Tasas de conversión actualizadas en el servidor');
    } catch (error) {
      console.error('❌ Error actualizando tasas de conversión:', error);
      alert('Error al actualizar las tasas de conversión. Por favor, intenta nuevamente.');
    }
  };

  // Función para migrar datos desde localStorage
  const handleMigrateFromLocalStorage = async () => {
    if (!window.confirm('¿Deseas migrar todos los datos desde localStorage al servidor?\n\nEsto guardará todas tus transacciones, tasas de conversión y cierre mensual en la base de datos del servidor.')) {
      return;
    }

    try {
      const result = await migrationApi.migrateFromLocalStorage();
      console.log('✅ Migración completada:', result);
      alert('✅ Datos migrados exitosamente al servidor!');

      // Recargar datos desde el servidor
      await loadDataFromServer();
      setMigrationNeeded(false);
    } catch (error) {
      console.error('❌ Error en migración:', error);
      alert('Error al migrar los datos. Por favor, intenta nuevamente.');
    }
  };

  // Función para importar desde Google Sheets - Año completo
  const handleImportFromGoogleSheets = async () => {
    const year = prompt('¿Qué año deseas importar desde Google Sheets?\n\nEjemplo: 2024', new Date().getFullYear().toString());

    if (!year) return;

    const confirm = window.confirm(
      `¿Importar TODOS los meses del año ${year} desde Google Sheets?\n\n` +
      `Esto cargará todas las transacciones encontradas en las hojas de Google Sheets.\n\n` +
      `Si ya tienes transacciones del ${year}, se agregarán las nuevas (no se duplicarán).`
    );

    if (!confirm) return;

    try {
      console.log(`📥 Importando año ${year} desde Google Sheets...`);
      const result = await googleSheetsApi.importYear(parseInt(year));

      console.log('✅ Importación completada:', result);
      alert(result.message || `✅ Importadas ${result.totalCount} transacciones de ${result.monthsImported} meses!`);

      // Recargar datos desde el servidor
      await loadDataFromServer();
    } catch (error) {
      console.error('❌ Error importando desde Google Sheets:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert(`Error al importar: ${errorMsg}`);
    }
  };

  // Función para importar un mes específico desde Google Sheets
  const handleImportMonthFromGoogleSheets = async () => {
    const year = prompt('¿Año?', new Date().getFullYear().toString());
    if (!year) return;

    const month = prompt('¿Mes? (1-12)', (new Date().getMonth() + 1).toString());
    if (!month) return;

    const monthNum = parseInt(month);
    if (monthNum < 1 || monthNum > 12) {
      alert('El mes debe estar entre 1 y 12');
      return;
    }

    const monthNames = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    const confirm = window.confirm(
      `¿Importar ${monthNames[monthNum - 1]} ${year} desde Google Sheets?`
    );

    if (!confirm) return;

    try {
      console.log(`📥 Importando ${monthNames[monthNum - 1]} ${year}...`);
      const result = await googleSheetsApi.importMonth(parseInt(year), monthNum);

      console.log('✅ Importación completada:', result);
      alert(result.message || `✅ Importadas ${result.count} transacciones!`);

      // Recargar datos desde el servidor
      await loadDataFromServer();
    } catch (error) {
      console.error('❌ Error importando desde Google Sheets:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert(`Error al importar: ${errorMsg}`);
    }
  };

  // Función para importar desde archivo Excel
  const handleImportFromExcel = async () => {
    // Crear un input file oculto
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';

    input.onchange = async (e) => {
      const file = e.target.files?.[0];

      if (!file) return;

      // Validar que sea un archivo Excel
      if (!file.name.match(/\.(xlsx|xls)$/i)) {
        alert('Solo se permiten archivos Excel (.xlsx, .xls)');
        return;
      }

      // Validar tamaño (máx 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('El archivo es demasiado grande. Máximo 10MB.');
        return;
      }

      const confirm = window.confirm(
        `¿Importar datos desde el archivo "${file.name}"?\n\n` +
        `Se importarán todas las hojas que tengan el formato correcto (Enero 2024, Febrero 2024, etc.)`
      );

      if (!confirm) return;

      try {
        console.log(`📂 Subiendo archivo: ${file.name}`);

        const result = await excelImportApi.importFromFile(file);

        console.log('✅ Importación completada:', result);

        if (result.totalCount > 0) {
          alert(
            `Importación exitosa!\n\n` +
            `• ${result.totalCount} transacciones importadas\n` +
            `• ${result.sheetsProcessed} de ${result.totalSheets} hojas procesadas`
          );

          // Recargar datos desde el servidor
          await loadDataFromServer();
        } else {
          alert(
            `No se encontraron transacciones para importar.\n\n` +
            `Verifica que las hojas tengan el formato correcto:\n` +
            `- Nombres: "Enero 2024", "Febrero 2024", etc.\n` +
            `- Columnas: Día, Food ($), Food Ref, Go Out ($), etc.`
          );
        }

      } catch (error) {
        console.error('❌ Error importando desde Excel:', error);
        const errorMsg = error.response?.data?.error || error.message;
        alert(`Error al importar: ${errorMsg}`);
      }
    };

    // Trigger click en el input
    input.click();
  };

  const handleNavigation = (page) => {
    setCurrentPage(page);
  };

  // Mostrar pantalla de carga mientras se cargan los datos
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Cargando datos desde el servidor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${uiVersion === 'v3' ? 'v3-root' : 'bg-gray-50'}`}>
      {/* Header de navegación */}
      <Header onNavigate={handleNavigation} currentPage={currentPage} />

      {/* Banner de migración */}
      {migrationNeeded && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-yellow-500 text-white px-6 py-4 rounded-lg shadow-lg max-w-2xl">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-semibold mb-1">Migración Disponible</p>
              <p className="text-sm">Se encontraron datos en localStorage. Migra tus datos al servidor para acceder desde cualquier dispositivo.</p>
            </div>
            <button
              onClick={handleMigrateFromLocalStorage}
              className="flex-shrink-0 bg-white text-yellow-600 font-semibold px-4 py-2 rounded hover:bg-yellow-50 transition-colors"
            >
              Migrar Ahora
            </button>
            <button
              onClick={() => setMigrationNeeded(false)}
              className="flex-shrink-0 text-white hover:text-yellow-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Indicador de sincronización flotante */}
      {(showSyncIndicator || syncing) && (
        <div className="fixed top-4 right-4 z-50 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 animate-pulse">
          <div className="w-3 h-3 bg-white rounded-full animate-bounce"></div>
          <span className="text-sm font-medium">Sincronizando com Google Sheets...</span>
        </div>
      )}

      {/* Main container */}
      {currentPage === 'dashboard' && (
        <div className="modern-container fade-in">
          {/* Modern Header - Aligned with other modules */}
          {uiVersion === 'v2' && (
          <div className="mb-8">
        <div className="expense-card-dark">
          <div className="flex items-center justify-center mb-4">
            <div className="h-1 w-16 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mr-4"></div>
            <p className="text-white font-medium text-2xl">Saver v2.0</p>
            <div className="h-1 w-16 bg-gradient-to-r from-pink-400 to-purple-400 rounded-full ml-4"></div>
          </div>
          <p className="text-sm text-gray-300 mb-4 text-center">
            Desenvolvido por Martin Saracco para gestão eficiente de gastos detectados por IA
          </p>
          <div className="flex flex-wrap justify-center gap-2 text-xs px-8">
            <span className="badge-success">Exportação Excel</span>
            <span className="badge-info">Gráficos Orçamento</span>
            <span className="badge-warning">Importação GPT</span>
            <span className="badge-error">Filtros Avançados</span>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            <button
              onClick={handleImportFromGoogleSheets}
              className="text-xs text-green-300 hover:text-green-100 bg-green-900 bg-opacity-50 px-3 py-1 rounded transition-colors font-semibold"
            >
              Importar Año Completo (Google Sheets)
            </button>
            <button
              onClick={handleImportMonthFromGoogleSheets}
              className="text-xs text-green-300 hover:text-green-100 bg-green-900 bg-opacity-50 px-3 py-1 rounded transition-colors"
            >
              Importar Un Mes (Google Sheets)
            </button>
            <button
              onClick={handleImportFromExcel}
              className="text-xs text-purple-300 hover:text-purple-100 bg-purple-900 bg-opacity-50 px-3 py-1 rounded transition-colors font-semibold"
            >
              Importar Archivo Excel
            </button>
            <button
              onClick={debugSpecificData}
              className="text-xs text-blue-300 hover:text-blue-100 bg-blue-900 bg-opacity-50 px-3 py-1 rounded transition-colors"
            >
              Debug Dados
            </button>
            <button
              onClick={clearAllLocalStorage}
              className="text-xs text-red-300 hover:text-red-100 bg-red-900 bg-opacity-50 px-3 py-1 rounded transition-colors"
            >
              Limpar Dados
            </button>
          </div>
        </div>
      </div>
          )}
          {uiVersion === 'v3' && (
          <div className="mb-8 v3-card-dark rounded-3xl p-6 md:p-8 shadow-2xl">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs font-semibold tracking-widest text-slate-400 uppercase mb-1">Saver v3.0</p>
                <h1 className="text-3xl md:text-4xl font-bold text-white">Tus finanzas</h1>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={handleImportFromGoogleSheets} className="px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 transition">
                  Año completo (Sheets)
                </button>
                <button onClick={handleImportMonthFromGoogleSheets} className="px-4 py-2 rounded-full text-sm font-semibold bg-white/5 text-slate-200 hover:bg-white/10 transition">
                  Un mes (Sheets)
                </button>
                <button onClick={handleImportFromExcel} className="px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/20 hover:shadow-fuchsia-500/40 transition">
                  Archivo Excel
                </button>
                <button onClick={debugSpecificData} className="px-3 py-2 rounded-full text-xs bg-white/5 text-slate-400 hover:bg-white/10 transition">
                  Debug
                </button>
                <button onClick={clearAllLocalStorage} className="px-3 py-2 rounded-full text-xs bg-red-500/10 text-red-300 hover:bg-red-500/20 transition">
                  Limpiar
                </button>
              </div>
            </div>
          </div>
          )}

      {/* Bulk Import - Full width and expanded */}
      <div className="hover-lift mb-8">
        <BulkImportComp
          onAddTransactions={handleAddMultipleTransactions}
          conversionRates={conversionRates}
          defaultExpanded={true}
        />
      </div>

      {/* Conversion Rates - Full width but minimized */}
      <div className="hover-lift mb-8">
        <ConversionRates 
          rates={conversionRates}
          onRatesChange={handleRatesChange}
          defaultMinimized={true}
        />
      </div>

      {/* Bottom modules - 2 columns grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        
        {/* Transaction Form (minimized) */}
        <div className="hover-lift">
          <TransactionForm 
            onAddTransaction={handleAddTransaction}
            conversionRates={conversionRates}
            defaultMinimized={true}
          />
        </div>

        {/* Excel Export (minimized) */}
        <div className="hover-lift">
          <ExcelExportPanel 
            transactions={transactions}
            defaultMinimized={true}
          />
        </div>

      </div>

      {/* Budget Charts Section - Full width and larger */}
      <div className="hover-lift mb-8">
        <BudgetChartsComp
          transactions={transactions}
          conversionRates={conversionRates}
        />
      </div>

      {/* Full Width Transactions List */}
      <div className="hover-lift mb-8">
        <TransactionsListComp
          transactions={transactions}
          onDeleteTransaction={handleDeleteTransaction}
        />
      </div>

      {/* Month Closure - Full width at the bottom */}
      <div className="hover-lift">
        <MonthClosure 
          conversionRates={conversionRates}
        />
      </div>


        </div>
      )}

      {/* Settings Page */}
      {currentPage === 'settings' && (
        <Settings />
      )}
    </div>
  );
};

// Componente principal con autenticación
function App() {
  return (
    <AuthProvider>
      <PrivateRoute>
        <MainApp />
      </PrivateRoute>
    </AuthProvider>
  );
}

export default App; 
